import { computed, nextTick, ref, watch } from 'vue'
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside, useTimeoutFn } from '@vueuse/core'

import type { ComputedRef, CSSProperties, Ref } from 'vue'
import type { AppDatePickerProps } from '../types/interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'

interface AppDatePickerOptions {
  props: AppDatePickerProps
  onChange: (value: string) => void
}

interface AppDatePickerReturn {
  refIsOpen: Ref<boolean>
  refControl: Ref<HTMLElement | null>
  refTrigger: Ref<HTMLElement | null>
  refDropdown: Ref<HTMLElement | null>
  refDropdownStyles: Ref<CSSProperties>
  refDate: Ref<Date | null>
  refMinDate: ComputedRef<Date | null>
  displayText: ComputedRef<string>
  hasValue: ComputedRef<boolean>
  toggle: () => void
  toggleWithKeyboard: () => Promise<void>
  close: () => void
  change: (date: Date | null) => void
  clear: () => void
}

const dayjs = useDayjs()

const toDate = (value?: string | null): Date | null => {
  if (!value) return null

  const date = dayjs(value)
  return date.isValid() ? date.toDate() : null
}

export const useAppDatePicker = ({ props, onChange }: AppDatePickerOptions): AppDatePickerReturn => {
  const refIsOpen = ref<boolean>(false)
  const refDate = ref<Date | null>(toDate(props.value))
  const refControl = ref<HTMLElement | null>(null)
  const refTrigger = ref<HTMLElement | null>(null)
  const refDropdown = ref<HTMLElement | null>(null)

  // Fixed, not absolute: inside a modal the content box scrolls, and an absolute calendar became part of that scroll
  // and was cut off at its edge. A fixed one sits over the modal, opens upwards when there is no room below, and
  // follows the field on scroll and resize.
  const { floatingStyles: refDropdownStyles } = useFloating(refTrigger, refDropdown, {
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
    ],
  })

  const refMinDate = computed<Date | null>(() => toDate(props.minDate))

  const hasValue = computed<boolean>(() => !!refDate.value)

  const displayText = computed<string>(() =>
    refDate.value ? dayjs(refDate.value).format(DateFormat.DefaultDate) : '')

  const toggle = (): void => {
    if (props.disabled) return

    if (refIsOpen.value) close()
    else refIsOpen.value = true
  }

  const toggleWithKeyboard = async (): Promise<void> => {
    if (props.disabled) return
    if (refIsOpen.value) {
      close()
      return
    }

    refIsOpen.value = true
    await nextTick()
    refDropdown.value?.querySelector<HTMLElement>('.vc-day-content[tabindex="0"]')?.focus()
  }

  // A day picked with the mouse holds focus. Removed with the calendar, it would fire v-calendar's focusout on an
  // already empty month, which throws ("reading 'dayIndex'"); handing focus back to the field first lets that
  // focusout run while the month is still there, and keeps keyboard users on the field.
  const close = (): void => {
    const focused = document.activeElement
    if (focused instanceof HTMLElement && refDropdown.value?.contains(focused)) refTrigger.value?.focus()

    refIsOpen.value = false
  }

  // The value is taken at once; the calendar closes once the click that picked the day is over, so v-calendar
  // finishes its own update of that click on a month that is still there.
  const { start: closeAfterClick } = useTimeoutFn(close, 0, { immediate: false })

  const change = (date: Date | null): void => {
    refDate.value = date
    onChange(date ? dayjs(date).format(DateFormat.DefaultDate) : '')
    closeAfterClick()
  }

  const clear = (): void => {
    if (props.disabled) return

    refDate.value = null
    onChange('')
  }

  watch(
    () => props.value,
    (value) => { refDate.value = toDate(value) },
  )

  onClickOutside(refControl, () => {
    if (refIsOpen.value) close()
  }, { ignore: [refDropdown] })

  return {
    refIsOpen,
    refControl,
    refTrigger,
    refDropdown,
    refDropdownStyles,
    refDate,
    refMinDate,
    displayText,
    hasValue,
    toggle,
    toggleWithKeyboard,
    close,
    change,
    clear,
  }
}
